import React, { useState } from 'react';
import * as motion from 'motion/react-m';
import { FormattedMessage } from 'react-intl';
import { H3 } from '@osmosis/react-ui/components/Text';
import { Flex } from '@osmosis/react-ui/components/Box';
import { bgPrimaryLightest } from '@osmosis/tailwind-ui/styles/background';
import { useQuizContext } from '../../../../../context/QuizContext';
import {
  FillInTheBlankKeytakeawayItem,
  MultipleChoiceData,
} from '../../../../../types/quiz';

const SuicideLifelineInfoComponent = () => (
  <Flex
    className={`p-3 rounded-md border border-blue-400 ${bgPrimaryLightest}`}
  >
    <FormattedMessage
      id="lifeline.text"
      defaultMessage="If you or someone you know needs this, here's the number for the U.S. National Suicide Prevention Lifeline: 1-800-273-8255 or text HOME to the Crisis Text Line at 741741, or visit www.iasp.info/suicidalthoughts/  for international resources. You are not alone."
    />
  </Flex>
);

import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from '@osmosis/nextjs-components/Accordion';
import useHtmlImageViewer from '../../../../../hooks/useHtmlImageViewer';
import useModal from '../../../../../hooks/useModal';
import ImageViewer from '../../../../ImageViewer';
import { slideHorizontal } from '../../../../../styles/animation';
import LearnifyText from '../../../../learn/LearnifyText';
import { Timer } from './Timer';
import Modal from '../../../../Modal';

const EXPLANATION_CONTAINER_ID = 'explanation-container';

/**
 * Explanation for quiz.
 */
export const Explanation = () => {
  const { currentItem, isDailyPractice } = useQuizContext();
  const [image, setImage] = useState<string>();
  const showLearnifyLinks = true;

  const imageViewer = useModal();

  const data =
    currentItem?.data ||
    ({} as {
      keyTakeaway?: string;
      keyTakeaways?: FillInTheBlankKeytakeawayItem[];
    });

  const keyTakeaway = 'keyTakeaway' in data ? data.keyTakeaway : '';
  const keyTakeaways = 'keyTakeaways' in data ? data.keyTakeaways : [];

  const mainExplanation = (data as MultipleChoiceData)?.mainExplanation;
  const showLifeline =
    (data as MultipleChoiceData)?.showLifeline === 'true' || false;

  const handleClickImage = (src: string) => {
    setImage(src);
    imageViewer.show();
  };

  useHtmlImageViewer(EXPLANATION_CONTAINER_ID, handleClickImage);

  return (
    <>
      <Modal isVisible={imageViewer.isVisible} hide={imageViewer.toggle}>
        <ImageViewer onClose={imageViewer.hide} src={image} />
      </Modal>
      <motion.div {...slideHorizontal()}>
        <Flex
          flexDirection="column"
          bg="gray5"
          p={3}
          mx={4}
          sx={{
            borderRadius: '5px',
            position: 'relative',
            zIndex: 0,
            gap: 3,
          }}
          data-id={EXPLANATION_CONTAINER_ID}
        >
          {isDailyPractice && <Timer />}
          {showLifeline && <SuicideLifelineInfoComponent />}
          {keyTakeaway && (
            <Flex flexDirection="column" sx={{ gap: 3 }}>
              <H3>
                <FormattedMessage
                  id="quiz.explanation.takeaway"
                  defaultMessage="Major takeaway"
                />
              </H3>
              <LearnifyText text={keyTakeaway} enabled={showLearnifyLinks} />
            </Flex>
          )}
          {keyTakeaways &&
            keyTakeaways.map(({ title, takeaway }) => {
              return (
                /* eslint-disable @nx/workspace/a11y/require-aria-expanded-for-collapsible -- aria-expanded is already set internally by these components from AccordionProvider context; the rule can't resolve the package-aliased import */
                <Accordion isMulti key={title}>
                  <AccordionItem>
                    <AccordionButton>{title}</AccordionButton>
                    <AccordionPanel>{takeaway.s}</AccordionPanel>
                  </AccordionItem>
                </Accordion>
                /* eslint-enable @nx/workspace/a11y/require-aria-expanded-for-collapsible */
              );
            })}
          {mainExplanation && (
            <Flex flexDirection="column" sx={{ gap: 3 }}>
              <H3>
                <FormattedMessage
                  id="quiz.explanation.explanation"
                  defaultMessage="Main explanation"
                />
              </H3>
              <LearnifyText
                text={mainExplanation}
                color="gray60"
                enabled={showLearnifyLinks}
              />
            </Flex>
          )}
        </Flex>
      </motion.div>
    </>
  );
};
